import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { getErrorMessage } from '../../lib/errors';
import { clearStoredDevice, unsubscribe } from '../../lib/push';
import { supabase } from '../../lib/supabase';
import type { BusinessRead } from '../../types/api';
import { deleteAccount, deleteBusiness } from '../onboarding/accountApi';
import { brandKindCopy } from '../onboarding/brandKind';
import { useAuth } from '../auth/useAuth';
import { useAccount } from '../onboarding/useAccount';

type Pending = 'brand' | 'account' | null;

interface DangerZoneProps {
  business: BusinessRead | null;
  email: string | null;
}

/**
 * The two deletes that cannot be undone, kept together and apart from
 * everything else on the page. Each one asks the user to retype what they are
 * about to lose: a brand's name, or the account's email address.
 *
 * Deleting the active brand takes its knowledge base, plans, history and
 * connections with it; the next brand becomes active, or setup reopens when
 * it was the last. Deleting the account takes every brand the user owns and
 * the sign-in itself, then signs this device out.
 */
export function DangerZone({ business, email }: DangerZoneProps) {
  const { businesses, removeBusiness } = useAccount();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState<Pending>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isOwner = business?.role === 'owner';
  const copy = brandKindCopy(business?.kind);
  const ownedCount = businesses.filter((entry) => entry.role === 'owner').length;

  function open(next: Pending) {
    setError(null);
    setPending(next);
  }

  async function confirmBrand() {
    if (!business) return;
    setIsBusy(true);
    setError(null);
    try {
      await deleteBusiness(business.id);
      removeBusiness(business.id);
      setPending(null);
      void navigate('/', { replace: true });
    } catch (caught) {
      setError(getErrorMessage(caught, 'Could not delete the brand. Try again.'));
    } finally {
      setIsBusy(false);
    }
  }

  async function confirmAccount() {
    setIsBusy(true);
    setError(null);
    try {
      await deleteAccount();
      // The server dropped this account's push rows with it; drop this
      // browser's subscription and the note of its row too.
      if (user) clearStoredDevice(user.id);
      await unsubscribe();
      // The user no longer exists server-side; the local session is all that
      // is left, and a failed sign-out call must not strand it on screen.
      try {
        await supabase.auth.signOut();
      } catch {
        await supabase.auth.signOut({ scope: 'local' });
      }
      void navigate('/login', { replace: true });
    } catch (caught) {
      setError(getErrorMessage(caught, 'Could not delete the account. Try again.'));
      setIsBusy(false);
    }
  }

  return (
    <section className="wizard settings-card settings-card--danger">
      <div className="settings-card__head">
        <h2 className="wizard__title">Delete</h2>
        <p className="settings-card__lede">
          Neither of these can be undone. Each one asks you to retype what you are about to lose.
        </p>
      </div>

      <div className="danger-row">
        <div className="danger-row__text">
          <p className="danger-row__title">Delete {business ? business.name : 'this brand'}</p>
          <p className="danger-row__body">
            Removes this {copy.noun} and everything scoped to it: brand profile, plans, history,
            news sources and connected accounts.
            {ownedCount <= 1 && isOwner
              ? ' It is your last brand, so setup reopens afterwards.'
              : ''}
            {!isOwner && business ? ' Only the owner can delete a brand.' : ''}
          </p>
        </div>
        <button
          className="button button--danger"
          type="button"
          disabled={!business || !isOwner}
          onClick={() => open('brand')}
        >
          Delete brand
        </button>
      </div>

      <div className="danger-row">
        <div className="danger-row__text">
          <p className="danger-row__title">Delete account</p>
          <p className="danger-row__body">
            Removes your sign-in and every brand you own, with all of their data. You are signed out
            on this device at once.
          </p>
        </div>
        <button className="button button--danger" type="button" onClick={() => open('account')}>
          Delete account
        </button>
      </div>

      {pending === 'brand' && business ? (
        <ConfirmDialog
          title={`Delete ${business.name}?`}
          message={`This removes the ${copy.noun} and all of its data. There is no undo.`}
          confirmLabel="Delete brand"
          busyLabel="Deleting"
          challenge={{ expected: business.name, label: `Type ${business.name} to confirm` }}
          isBusy={isBusy}
          error={error}
          onConfirm={() => void confirmBrand()}
          onDismiss={() => (isBusy ? undefined : setPending(null))}
        />
      ) : null}

      {pending === 'account' ? (
        <ConfirmDialog
          title="Delete your account?"
          message="This removes your sign-in and every brand you own. There is no undo."
          confirmLabel="Delete account"
          busyLabel="Deleting"
          challenge={{
            expected: email ?? 'DELETE',
            label: email ? 'Type your email address to confirm' : 'Type DELETE to confirm',
          }}
          isBusy={isBusy}
          error={error}
          onConfirm={() => void confirmAccount()}
          onDismiss={() => (isBusy ? undefined : setPending(null))}
        />
      ) : null}
    </section>
  );
}
